// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseToolbar } from '~/components/common';
import { SettingBoxItem, SettingGoalModal } from '~/components/module/setting';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { usePermissions } from '~/utils/hooks/userPermissions';
import {
  CATEGORY_PERMISSIONS,
  CHECKLIST_PERMISSIONS,
  COUPON_PERMISSIONS,
  EVENT_TYPE_PERMISSIONS,
  ROLE_PERMISSIONS,
  USER_PERMISSIONS,
  VENUE_PERMISSIONS,
} from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './Settings.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const Settings = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/setting', 'common/base']);
  const navigate = useNavigate();

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];

  const { canAllUsers, canCreateUsers, canViewUsers, canDetailUsers, canEditUsers, canDeleteUsers } =
    usePermissions(USER_PERMISSIONS);
  const { canAllRoles, canCreateRoles, canViewRoles, canDetailRoles, canEditRoles, canDeleteRoles } =
    usePermissions(ROLE_PERMISSIONS);
  const { canAllCoupons, canCreateCoupons, canViewCoupons, canDetailCoupons, canEditCoupons, canDeleteCoupons } =
    usePermissions(COUPON_PERMISSIONS);
  const {
    canAllEventTypes,
    canCreateEventTypes,
    canViewEventTypes,
    canDetailEventTypes,
    canEditEventTypes,
    canDeleteEventTypes,
  } = usePermissions(EVENT_TYPE_PERMISSIONS);
  const { canAllCategory, canCreateCategory, canViewCategory, canDetailCategory, canEditCategory, canDeleteCategory } =
    usePermissions(CATEGORY_PERMISSIONS);
  const {
    canAllChecklists,
    canCreateChecklists,
    canViewChecklists,
    canDetailChecklists,
    canEditChecklists,
    canDeleteChecklists,
  } = usePermissions(CHECKLIST_PERMISSIONS);
  const { canAllVenue, canCreateVenue, canViewVenue, canDetailVenue, canEditVenue, canDeleteVenue } =
    usePermissions(VENUE_PERMISSIONS);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isOpenGoalFormModal, setIsOpenGoalFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleNavigatePageUser = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.users);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.users);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.users);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.users);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.users);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.users);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.users);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.users);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.users);
        break;
    }
  };

  const handleNavigatePageRole = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.roleManagement);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.roleManagement);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.roleManagement);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.roleManagement);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.roleManagement);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.roleManagement);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.roleManagement);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.roleManagement);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.roleManagement);
        break;
    }
  };

  const handleNavigatePageCoupons = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.coupon);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.coupon);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.coupon);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.coupon);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.coupon);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.coupon);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.coupon);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.coupon);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.coupon);
        break;
    }
  };

  const handleNavigatePageEventTypes = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.eventType);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.eventType);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.eventType);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.eventType);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.eventType);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.eventType);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.eventType);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.eventType);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.eventType);
        break;
    }
  };

  const handleNavigatePageCategory = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.category);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.category);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.category);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.category);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.category);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.category);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.category);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.category);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.category);
        break;
    }
  };

  const handleNavigatePageChecklist = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.checklist);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.checklist);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.checklist);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.checklist);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.checklist);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.checklist);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.checklist);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.checklist);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.checklist);
        break;
    }
  };

  const handleNavigatePageVenue = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.venue);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.venue);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.venue);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.venue);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.venue);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.venue);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.venue);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.venue);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.venue);
        break;
    }
  };

  const handleNavigateModalSettingGoal = () => {
    handleShowGoalFormModal();
  };

  const handleShowGoalFormModal = () => {
    setIsOpenGoalFormModal(!isOpenGoalFormModal);
  };

  //#endregion Handle Function

  return (
    <Box id='settingsPage' className={cx('container')}>
      <Stack gap={2}>
        <BaseToolbar title={t('setting_page_title')} />

        <Typography className={cx('subTitle')}>{t('setting_page_sub_title')}</Typography>
      </Stack>

      <Grid2 rowSpacing={8} columnSpacing={7} container>
        {(canAllUsers || canCreateUsers || canViewUsers || canDetailUsers || canEditUsers || canDeleteUsers) && (
          <SettingBoxItem
            icon={icons.menuIconUser}
            title={t('setting_page_title_user')}
            subTitle={t('setting_page_sub_title_user')}
            onClick={handleNavigatePageUser}
          />
        )}

        {(canAllRoles || canCreateRoles || canViewRoles || canDetailRoles || canEditRoles || canDeleteRoles) && (
          <SettingBoxItem
            icon={icons.menuIconRole}
            title={t('setting_page_title_role')}
            subTitle={t('setting_page_sub_title_role')}
            onClick={handleNavigatePageRole}
          />
        )}

        {(canAllCoupons ||
          canCreateCoupons ||
          canViewCoupons ||
          canDetailCoupons ||
          canEditCoupons ||
          canDeleteCoupons) && (
          <SettingBoxItem
            icon={icons.menuIconCoupons}
            title={t('setting_page_title_coupons')}
            subTitle={t('setting_page_sub_title_coupons')}
            onClick={handleNavigatePageCoupons}
          />
        )}

        {(canAllEventTypes ||
          canCreateEventTypes ||
          canViewEventTypes ||
          canDetailEventTypes ||
          canEditEventTypes ||
          canDeleteEventTypes) && (
          <SettingBoxItem
            icon={icons.menuIconEventTypes}
            title={t('setting_page_title_event_types')}
            subTitle={t('setting_page_sub_title_event_types')}
            onClick={handleNavigatePageEventTypes}
          />
        )}

        {(canAllCategory ||
          canCreateCategory ||
          canViewCategory ||
          canDetailCategory ||
          canEditCategory ||
          canDeleteCategory) && (
          <SettingBoxItem
            icon={icons.menuIconCategories}
            title={t('setting_page_title_categories')}
            subTitle={t('setting_page_sub_title_categories')}
            onClick={handleNavigatePageCategory}
          />
        )}

        <SettingBoxItem
          icon={icons.menuIconGoal}
          title={t('setting_page_title_setting_goal')}
          subTitle={t('setting_page_sib_title_setting_goal')}
          onClick={handleNavigateModalSettingGoal}
        />

        {(canAllChecklists ||
          canCreateChecklists ||
          canViewChecklists ||
          canDetailChecklists ||
          canEditChecklists ||
          canDeleteChecklists) && (
          <SettingBoxItem
            icon={icons.menuIconChecklist}
            title={t('setting_page_title_checklists')}
            subTitle={t('setting_page_sub_title_checklists')}
            onClick={handleNavigatePageChecklist}
          />
        )}

        {(canAllVenue || canCreateVenue || canViewVenue || canDetailVenue || canEditVenue || canDeleteVenue) && (
          <SettingBoxItem
            icon={icons.menuIconVenue}
            title={t('setting_page_title_venue')}
            subTitle={t('setting_page_sub_title_venue')}
            onClick={handleNavigatePageVenue}
          />
        )}
      </Grid2>

      {isOpenGoalFormModal && <SettingGoalModal onClose={handleShowGoalFormModal} />}
    </Box>
  );
};

export default Settings;
